import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Button from "../../../../components/ui/Button";
import Icon from "../../../../components/AppIcon";

const VendorAssignModal = ({ isOpen, onClose, orderId, onSuccess, vendors = [], vendorsLoading = false }) => {
 
  const [selectedVendor, setSelectedVendor] = useState(null); // number
  const [loading, setLoading] = useState(false);
  const [fetchingVendors, setFetchingVendors] = useState(false);
  const [search, setSearch] = useState("");
  const [assignmentNotes, setAssignmentNotes] = useState("");
  const [error, setError] = useState("");



  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return vendors;

    return vendors.filter(
      (v) =>
        v.name?.toLowerCase().includes(s) ||
        v.city?.toLowerCase().includes(s) ||
        v.type?.toLowerCase().includes(s)
    );
  }, [vendors, search]);

  const formatVendorType = (type) => {
    switch (type?.toUpperCase()) {
      case "LAB":
        return "Laboratory";
      case "COLLECTION":
        return "Collection Center";
      case "DIAGNOSTIC":
        return "Diagnostic Center";
      default:
        return "Vendor";
    }
  };

  const handleAssign = async () => {
    if (!selectedVendor) return;

    try {
      setLoading(true);
      await axios.put(
        `${import.meta.env.VITE_API_BASE_URL}/api/orders/${orderId}/assign-vendor`,
        {
          vendorId: selectedVendor, // number
          notes: assignmentNotes.trim() || undefined,
        }
      );

      onSuccess?.();
      onClose?.();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to assign vendor");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Assign Vendor</h2>
              <p className="text-xs text-gray-500 mt-0.5">Select a vendor for Order #{orderId}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Search" size={14} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search vendors by name, city, or type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Loading/Error */}
          {fetchingVendors && (
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <Icon name="Loader" size={12} className="animate-spin" /> Loading vendors...
            </div>
          )}
          {error && <div className="text-xs text-red-600">{error}</div>}

          {/* Vendor Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
            {filtered.map((v) => {
              const isSelected = selectedVendor === v.id;

              return (
                <div
                  key={v.id}
                  onClick={() => setSelectedVendor(v.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`p-1.5 rounded ${isSelected ? "bg-blue-100" : "bg-gray-100"}`}>
                          <Icon
                            name="Building"
                            size={14}
                            className={isSelected ? "text-blue-600" : "text-gray-600"}
                          />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{v.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                              {formatVendorType(v.type)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5 text-xs">
                        <div className="flex items-center gap-2">
                          <Icon name="MapPin" size={12} className="text-gray-400" />
                          <span className="text-gray-600">{v.city || "Location not specified"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Phone" size={12} className="text-gray-400" />
                          <span className="text-gray-600">{v.number || "Contact not available"}</span>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`ml-2 w-5 h-5 rounded-full border flex items-center justify-center ${
                        isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                      }`}
                    >
                      {isSelected && <Icon name="Check" size={12} className="text-white" />}
                    </div>
                  </div>
                </div>
              );
            })}

            {!fetchingVendors && filtered.length === 0 && (
              <div className="col-span-2 text-center py-8">
                <Icon name="Search" size={24} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No vendors found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="pt-3 border-t border-gray-200">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Assignment Notes (Optional)
            </label>
            <textarea
              value={assignmentNotes}
              onChange={(e) => setAssignmentNotes(e.target.value)}
              placeholder="Add any specific instructions or notes for the vendor..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t border-gray-200">
            <Button variant="outline" onClick={onClose} className="flex-1 text-xs px-3 py-2 h-9">
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={loading || !selectedVendor}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 h-9"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-1.5">
                  <Icon name="Loader" size={12} className="animate-spin" />
                  Assigning...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5">
                  <Icon name="CheckCircle" size={12} />
                  Assign Vendor
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorAssignModal;
